import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { mockUsers } from "../../data";

// Single-file React + Tailwind component for a full-page Time Report
// - Uses recharts for live-updating Line and Donut charts
// - Tailwind CSS classes used for layout and styling
// - Default export a React component

export default function TimeReportPage() {
  // initial week labels (Sun..Sat)
  const days = ["S", "M", "T", "W", "T", "F", "S"];

  // seed data generator
  const generateData = () =>
    days.map((d, i) => ({
      day: d,
      hoursPlanned: +(
        3 +
        Math.sin(i + 0.5) * 1.5 +
        Math.random() * 1.2
      ).toFixed(2),
      hoursWorked: +(2.5 + Math.cos(i) * 1.3 + Math.random()).toFixed(2),
    }));

  const [series, setSeries] = useState(generateData);
  const [live, setLive] = useState(true);

  // simulate live updates every 2.5s while `live` is true
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => {
      setSeries((prev) => {
        // rotate values slightly to look "live"
        const next = prev.map((p) => ({ ...p }));
        const idx = Math.floor(Math.random() * next.length);
        next[idx].hoursWorked = +Math.max(
          0.1,
          (next[idx].hoursWorked + (Math.random() - 0.45)).toFixed(2)
        );
        next[idx].hoursPlanned = +Math.max(
          0.1,
          (next[idx].hoursPlanned + (Math.random() - 0.35)).toFixed(2)
        );
        return next;
      });
    }, 2500);
    return () => clearInterval(id);
  }, [live]);

  const totals = useMemo(() => {
    const worked = series.reduce((s, r) => s + r.hoursWorked, 0);
    const planned = series.reduce((s, r) => s + r.hoursPlanned, 0);
    const hh = Math.floor(worked);
    const mm = Math.round((worked - hh) * 60);
    const plannedH = Math.floor(planned);
    const plannedM = Math.round((planned - plannedH) * 60);
    return {
      workedStr: `${pad(hh)}:${pad(mm)}:12`, // keep seconds static like screenshot
      plannedStr: `${pad(plannedH)}:${pad(plannedM)}:10`,
      worked,
      planned,
    };
  }, [series]);

  const donutData = useMemo(() => {
    const covered = Math.min(
      100,
      Math.round((totals.worked / Math.max(1, totals.planned)) * 100)
    );
    return [
      { name: "covered", value: covered },
      { name: "undone", value: 100 - covered },
    ];
  }, [totals]);

  const COLORS = ["#6C63FF", "#E8EDFF"];

  return (
    <div className="min-h-screen font-Outfit">
      <div className="max-w-7xl mx-auto rounded-lg shadow-sm overflow-hidden">
        {/* Header area */}
        <div className="grid bg-white grid-cols-12 gap-6 p-8 items-center">
          <div className="col-span-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Ella’s Time Reports{" "}
              <span className="text-sm text-gray-400">
                18/04/2024 - 24/04/2024
              </span>
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-y-4 gap-x-8">
              <div className="flex items-center">
                <div className="w-28 text-sm font-semibold text-indigo-900">
                  Client -
                </div>
                <div className="text-sm text-gray-600">CeHR</div>
              </div>
              <div className="flex items-center">
                <div className="w-28 text-sm font-semibold text-indigo-900">
                  Project -
                </div>
                <div className="text-sm text-gray-600">
                  E-commerce App Project
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-28 text-sm font-semibold text-indigo-900">
                  Tasks -
                </div>
                <div className="text-sm text-gray-600">10 tasks</div>
              </div>
              <div className="flex items-center">
                <div className="w-28 text-sm font-semibold text-indigo-900">
                  Team -
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <div>
                    <p>10 members</p>
                  </div>

                  <div className="flex -space-x-3">
                    {Object.values(mockUsers).map((user) => (
                      <img
                        key={user.id}
                        src={user.avatar}
                        // alt={`user-${i}`}
                        alt={user.name}
                        className="w-8 h-8 rounded-full border-2 border-white object-cover"
                      />
                    ))}
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-700 border-2 border-white">
                      +5
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-6 border-l border-indigo-700 pl-8">
            <div className=" flex  justify-between ">
              <div className="flex flex-col items-center justify-between">
                <div>
                  <div className="text-xs text-gray-500">
                    Total Hours Worked This Week
                  </div>
                  <div className="text-4xl font-extrabold text-indigo-700 mt-2">
                    {totals.workedStr}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mt-6">
                    Total Hours Workable This Week
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-2">
                    {totals.plannedStr}
                  </div>
                </div>
              </div>

              <div>
                <div className="mt-6 w-20 h-20 rounded-full bg-gradient-to-br from-teal-200 to-cyan-600 flex items-center justify-center">
                  <div className="bg-white w-12 h-12 rounded-full flex items-center justify-center text-cyan-600 font-bold">
                    ⏱
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    onClick={() => setLive(!live)}
                    className="px-4 py-1 bg-indigo-600 text-white rounded-md text-sm"
                  >
                    {live ? "Stop Live" : "Start Live"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 p-6">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-8 bg-white rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Activity</h3>
                <div className="text-sm text-gray-500">7 (Hours)</div>
              </div>
              <div className="h-64">
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >
                  <LineChart
                    data={series}
                    margin={{ top: 10, right: 10, left: -20, bottom: 10 }}
                  >
                    <XAxis
                      dataKey="day"
                      tick={{ fill: "#111827", fontSize: 12 }}
                    />
                    <YAxis tick={{ fill: "#111827", fontSize: 12 }} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="hoursPlanned"
                      stroke="#CBD5E1"
                      strokeWidth={4}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="hoursWorked"
                      stroke="#111827"
                      strokeWidth={4}
                      dot={{ stroke: "#6C63FF", strokeWidth: 4, r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Donut chart */}
            <div className="col-span-4 bg-gray-50 rounded-lg p-6 flex flex-col items-center justify-center">
              <div className="w-full flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-semibold">
                    Hours covered Overall
                  </h3>
                  <div className="text-sm text-gray-500">
                    Percentage covered / Percentage undone
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {donutData[0].value}%
                </div>
              </div>

              <div className="w-52 h-52">
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >
                  <PieChart>
                    <Pie
                      data={donutData}
                      innerRadius={64}
                      outerRadius={96}
                      paddingAngle={3}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                    >
                      {donutData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-4 text-center">
                <div className="text-2xl font-bold text-indigo-700">
                  {donutData[0].value}%
                </div>
                <div className="text-sm text-gray-500">covered</div>
              </div>
            </div>
          </div>

          {/* <div className="mt-8 grid grid-cols-3 gap-6">
            <StatCard
              title="Most Active Day"
              value={`${
                series.reduce((a, b) => (a.hoursWorked > b.hoursWorked ? a : b))
                  .day
              } (${Math.max(...series.map((s) => s.hoursWorked)).toFixed(1)}h)`}
            />
            <StatCard
              title="Least Active Day"
              value={`${
                series.reduce((a, b) => (a.hoursWorked < b.hoursWorked ? a : b))
                  .day
              } (${Math.min(...series.map((s) => s.hoursWorked)).toFixed(1)}h)`}
            />
            <StatCard
              title="Team Size"
              value={`10 members`}
            />
          </div> */}
        </div>
      </div>
    </div>
  );
}

// function StatCard({ title, value }) {
//   return (
//     <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-between">
//       <div>
//         <div className="text-sm text-gray-500">{title}</div>
//         <div className="text-lg font-semibold text-gray-800">{value}</div>
//       </div>
//       <div className="text-indigo-600 font-bold">›</div>
//     </div>
//   );
// }

function pad(n) {
  return String(n).padStart(2, "0");
}
